import React from "react";

import dayjs from "dayjs-local";
import { DeviceByTypeData, isDeviceOnline } from "modules/devices";
export enum DeviceChargingState {
  Charging = "Charging",
  Discharging = "Discharging",
  Idle = "Idle",
  Offline = "Offline",
}
const thresholdForChargeDischargeInWatts = 1000;

export const getDeviceChargingState = (
  onlineDevices: DeviceByTypeData[]
): DeviceChargingState => {
  if (onlineDevices.length === 0) return DeviceChargingState.Offline;

  const combinedPower = onlineDevices.reduce(
    (x, y) => x + y.grid_current_a * y.grid_voltage_v,
    0
  );

  if (combinedPower > thresholdForChargeDischargeInWatts) {
    return DeviceChargingState.Charging;
  } else if (combinedPower < -thresholdForChargeDischargeInWatts) {
    return DeviceChargingState.Discharging;
  } else {
    return DeviceChargingState.Idle;
  }
};

export const getAggregatedDeviceStats = (
  deviceData: DeviceByTypeData[],
  getNow: () => number
) => {
  // Remove the unit controller from stats calc
  deviceData = deviceData.filter(
    (x) =>
      x.device_id &&
      !(x.device_id.includes("_UC") || x.device_id.includes("_Un"))
  );
  const onlineDevices = deviceData.filter((x) =>
    isDeviceOnline(dayjs.utc(x.timestamp_utc), getNow())
  );

  const grid_power_kva = onlineDevices.reduce(
    (x, y) => x + (y.grid_current_a * y.grid_voltage_v) / 1000,
    0
  );
  const deviceChargingState = getDeviceChargingState(onlineDevices);
  const cell_temperature_median_avg_c =
    onlineDevices.reduce((x, y) => x + y.cell_temperature_median_c, 0) /
    onlineDevices.length;
  const pack_soc_average_ah_pct =
    Math.min(
      onlineDevices.reduce(
        (x, y) => x + (y.pack_soc_kwh_pct || y.pack_soc_ah_pct || 0) * 100,
        0
      ) / onlineDevices.length,
      99.99
    ) || 0;
  const pack_soh_average_ah_pct =
    onlineDevices.reduce(
      (x, y) => x + (y.pack_soh_kwh_pct || y.pack_soh_ah_pct || 0) * 100,
      0
    ) / onlineDevices.length || 0;

  return {
    grid_power_kva,
    cell_temperature_median_avg_c,
    pack_soc_average_ah_pct,
    pack_soh_average_ah_pct,
    deviceChargingState,
  };
};

export const AggregatedDeviceStats = ({
  deviceData,
  getNow,
  isUnit = false,
}: {
  deviceData: DeviceByTypeData[];
  getNow: () => number;
  isUnit?: boolean;
}) => {
  const {
    grid_power_kva,
    cell_temperature_median_avg_c,
    pack_soc_average_ah_pct,
    pack_soh_average_ah_pct,
    deviceChargingState,
  } = getAggregatedDeviceStats(deviceData, getNow);

  if (!deviceData)
    return <div className="text-gray-700 text-base">Loading...</div>;

  // const uc = deviceData.find(
  //   (x) =>
  //     x.device_id &&
  //     (x.device_id.endsWith("_UC") || x.device_id.endsWith("_Un"))
  // );

  return (
    <div className="text-gray-700 font-bold text-lg">
      <div className="flex flex-no-wrap pt-4">
        <div className="">
          <div className="block ">
            <BatteryPower powerInKW={grid_power_kva} />
          </div>
          <div className="block pt-1">
            <BatteryProgressBar
              percent={pack_soc_average_ah_pct}
              label={`state of charge`}
              labelInside={` - ${deviceChargingState}`}
            />
          </div>
          {/* {isUnit && (
            <div className="block pt-2">
              <UnitOpMode
                mode={uc?.state}
                online={isDeviceOnline(dayjs.utc(uc?.timestamp_utc), getNow())}
              />
            </div>
          )} */}
        </div>
        <div className="ml-12">
          <div className="block">
            <BatteryTemperature temperature={cell_temperature_median_avg_c} />
          </div>
          <div className="block pt-1">
            <BatteryHealth percentageStateOfHealth={pack_soh_average_ah_pct} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const AggregatedDeviceStatsRow = ({
  deviceData,
  getNow,
  isUnit = false,
}: {
  deviceData: DeviceByTypeData[];
  getNow: () => number;
  isUnit?: boolean;
}) => {
  const {
    grid_power_kva,
    cell_temperature_median_avg_c,
    pack_soc_average_ah_pct,
    pack_soh_average_ah_pct,
    deviceChargingState,
  } = getAggregatedDeviceStats(deviceData, getNow);

  // const uc = deviceData.find((x) => x.device_id && x.device_id.endsWith("UC"));

  if (!deviceData)
    return (
      <div className="text-gray-700 px-6 py-4 text-base">Loading stats...</div>
    );
  return (
    <div>
      <div className="text-gray-700 font-bold text-lg flex flex-wrap">
        <div className="w-48 px-4 py-2 mb-2 rounded-lg shadow-sm bg-white mr-4 items">
          <BatteryProgressBar
            percent={pack_soc_average_ah_pct}
            label={`state of charge`}
            labelInside={` - ${deviceChargingState}`}
          />
        </div>
        <div className="w-48 px-4 py-2 mb-2 rounded-lg shadow-sm bg-white mr-4">
          <BatteryPower powerInKW={grid_power_kva} />
        </div>
        <div className="w-48 px-4 py-2 mb-2 rounded-lg shadow-sm bg-white mr-4">
          <BatteryTemperature temperature={cell_temperature_median_avg_c} />
        </div>
        <div className="w-48 px-4 py-2 mb-2 rounded-lg shadow-sm bg-white">
          <BatteryHealth percentageStateOfHealth={pack_soh_average_ah_pct} />
        </div>
      </div>
      {/* {isUnit && (
        <div className="text-gray-700 font-bold text-lg flex flex-wrap pt-2">
          <div className="w-48 px-4 py-2 mb-2 rounded-lg shadow-sm bg-white mr-4 items">
            <UnitOpMode
              mode={uc?.state}
              online={isDeviceOnline(dayjs.utc(uc?.timestamp_utc), getNow())}
            />
          </div>
          <div className="w-48 px-4 py-2 mb-2 rounded-lg shadow-sm bg-white mr-4">
            <Region region={"AUS C"} />
          </div>
        </div>
      )} */}
    </div>
  );
};

export const BatteryPower = ({ powerInKW }: { powerInKW: number }) => {
  return (
    <div className="inline-flex items-center flex-no-wrap">
      <svg viewBox="0 0 20 20" className="mr-1 h-4 w-4" fill="currentColor">
        <path d="M6.803,18.998c-0.194-0.127,3.153-7.16,3.038-7.469C9.725,11.22,6.176,10.093,6.003,9.55  c-0.174-0.543,7.007-8.707,7.196-8.549c0.188,0.158-3.129,7.238-3.039,7.469c0.091,0.23,3.728,1.404,3.838,1.979  C14.109,11.024,6.996,19.125,6.803,18.998z" />
      </svg>
      {powerInKW.toFixed(2)} kVA
    </div>
  );
};
export const BatteryTemperature = ({
  temperature,
}: {
  temperature: number;
}) => {
  return (
    <div className="inline-flex items-center flex-no-wrap">
      <svg viewBox="0 0 20 20" className="mr-1 h-4 w-4" fill="currentColor">
        <g stroke="none" fillRule="evenodd">
          <path
            d="M11,11.1707057 C12.1651924,11.5825421 13,12.6937812 13,14 C13,15.6568542 11.6568542,17 10,17 C8.34314575,17 7,15.6568542 7,14 C7,12.6937812 7.83480763,11.5825421 9,11.1707057 L9,7 L11,7 L11,11.1707057 Z M12,10.5351288 C13.1956027,11.2267476 14,12.5194353 14,14 C14,16.209139 12.209139,18 10,18 C7.790861,18 6,16.209139 6,14 C6,12.5194353 6.80439726,11.2267476 8,10.5351288 L8,4.0085302 C8,2.8992496 8.88772964,2 10,2 C11.1045695,2 12,2.90195036 12,4.0085302 L12,10.5351288 Z M14,9.52778699 C15.2274987,10.6264281 16,12.2230004 16,14 C16,17.3137085 13.3137085,20 10,20 C6.6862915,20 4,17.3137085 4,14 C4,12.2230004 4.77250128,10.6264281 6,9.52778699 L6,4.00002024 C6,1.79087006 7.79535615,0 10,0 C12.209139,0 14,1.79445134 14,4.00002024 L14,9.52778699 Z"
            id="Combined-Shape"
          ></path>
        </g>
      </svg>
      {temperature.toFixed(2)} °C
    </div>
  );
};
export const BatteryHealth = ({
  percentageStateOfHealth,
}: {
  percentageStateOfHealth: number;
}) => {
  return (
    <div
      title={`${percentageStateOfHealth.toFixed(2)}% state of health`}
      className="inline-flex items-center flex-no-wrap"
    >
      <svg
        viewBox="0 0 242.041 242.041"
        className="mr-1 h-4 w-4"
        fill="currentColor"
      >
        <g>
          <path d="M239.506,66.581c-8.201-32.165-35.386-54.63-66.11-54.63c-20.208,0-38.49,9.711-52.394,27.59   c-13.896-17.881-32.17-27.59-52.373-27.59c-30.712,0-57.89,22.464-66.092,54.628c-4.389,17.211-5.242,44.71,15.619,77.978   c19.272,30.734,52.666,59.203,99.251,84.616c1.12,0.61,2.355,0.916,3.592,0.916c1.236,0,2.472-0.306,3.591-0.916   c46.599-25.414,80.002-53.883,99.282-84.616C244.743,111.289,243.893,83.79,239.506,66.581z M121,214.025   C6.852,150.416,12.546,88.032,17.072,70.286c6.507-25.515,27.708-43.335,51.558-43.335c18.033,0,34.354,10.552,45.955,29.71   c1.359,2.244,3.791,3.615,6.414,3.615c0,0,0.001,0,0.001,0c2.623,0,5.055-1.37,6.414-3.613   c11.611-19.16,27.942-29.712,45.982-29.712c23.861,0,45.069,17.82,51.575,43.335C229.495,88.031,235.181,150.411,121,214.025z" />
          <path d="M195.118,121.907h-33.705l-12.538-46.403c-0.884-3.272-3.852-5.544-7.24-5.544c-0.001,0-0.003,0-0.005,0   c-3.391,0.002-6.358,2.279-7.238,5.554l-17.243,64.179L99.906,75.514c-0.872-3.246-3.797-5.515-7.158-5.554   c-0.028,0-0.057,0-0.086,0c-3.325,0-6.258,2.192-7.196,5.39l-8.728,29.771H42.904c-4.142,0-7.5,3.357-7.5,7.5s3.358,7.5,7.5,7.5   h39.451c3.33,0,6.26-2.195,7.197-5.39l2.796-9.538l17.558,65.351c0.88,3.276,3.851,5.554,7.243,5.554   c3.393,0,6.363-2.277,7.243-5.554l17.263-64.254l6.775,25.074c0.884,3.271,3.851,5.544,7.24,5.544h39.447   c4.142,0,7.5-3.357,7.5-7.5C202.618,125.265,199.261,121.907,195.118,121.907z" />
        </g>
      </svg>
      {percentageStateOfHealth.toFixed(2)}%
    </div>
  );
};
export const BatteryProgressBar = ({
  percent,
  label,
  labelInside,
}: {
  percent: number;
  label: string;
  labelInside: string;
}) => (
  <div className="flex flex-no-wrap w-40 h-full items-center">
    <div
      className="flex w-full h-6 border-gray-700 rounded-sm border-2 relative"
      title={`${percent.toFixed(2)}% ${label}`}
    >
      <div className="absolute h-6 w-full text-xs pt-px text-center">
        {percent.toFixed(2)}% {labelInside}
      </div>
      <div
        style={{ width: `${percent}%` }}
        className={(percent < 10
          ? "bg-red-400 m-px"
          : "bg-green-400 m-px"
        ).concat(" rounded-sm leading-none py-1 text-center")}
      ></div>
    </div>
    <div
      style={{ width: "3px" }}
      className="flex-none rounded-r-md w-px bg-gray-700 h-3"
    ></div>
  </div>
);

export const UnitOpMode = ({
  mode,
  online,
}: {
  mode?: string;
  online?: boolean;
}) => {
  return (
    <div title={`${mode}`} className="inline-flex items-center flex-no-wrap">
      {online && mode ? mode : "Unit Offline"}
    </div>
  );
};

export const Region = ({ region }: { region?: string }) => {
  return (
    <div
      title={`Set region: ${region}`}
      className="inline-flex items-center flex-no-wrap"
    >
      <svg viewBox="0 0 20 20" className="mr-1 h-4 w-4" fill="currentColor">
        <g>
          <path d="M10 17.583q-1.562 0-2.948-.593-1.385-.594-2.417-1.625-1.031-1.032-1.625-2.417-.593-1.386-.593-2.948 0-1.583.593-2.958.594-1.375 1.625-2.407Q5.667 3.604 7.052 3.01 8.438 2.417 10 2.417q1.583 0 2.958.593 1.375.594 2.407 1.625 1.031 1.032 1.625 2.417.593 1.386.593 2.948t-.593 2.948q-.594 1.385-1.625 2.417-1.032 1.031-2.417 1.625-1.386.593-2.948.593Zm-1-1.166V15q-.417 0-.708-.292Q8 14.417 8 14v-1L3.646 8.646q-.084.396-.115.729Q3.5 9.708 3.5 10q0 2.396 1.552 4.229Q6.604 16.062 9 16.417Zm6-2.271q.375-.438.656-.917.282-.479.469-1.01t.281-1.094q.094-.563.094-1.125 0-1.979-1.083-3.615Q14.333 4.75 12.5 3.979V4.5q0 .625-.438 1.062Q11.625 6 11 6H9v1q0 .417-.292.708Q8.417 8 8 8H7v2h5q.417 0 .708.292.292.291.292.708v2h.854q.479 0 .813.333.333.334.333.813Z" />
        </g>
      </svg>
      {region}
    </div>
  );
};
